import { z } from "zod";

const configSchema = z.object({
  apiBaseUrl: z.string().url(),
  cognitoPoolId: z.string().min(1, "Cognito Pool ID is required"),
  cognitoClientId: z.string().min(1, "Cognito Client ID is required"),
  cognitoRegion: z.string(),
  environment: z.enum(["development", "staging", "production"]),
  recaptchaSiteKey: z.string().optional(),
  /**
   * Origin of the public investor portal, used to build shareable property
   * links (MPPI-00035).
   *
   * OPTIONAL on purpose. Production `config.json` is written by hand and is
   * never overwritten by a deploy, so a required key would fail validation on
   * the live site the moment this shipped. When absent the origin is derived
   * from the admin host instead — see `portalOrigin()`.
   */
  portalBaseUrl: z.string().url().optional(),
  // Public Mapbox access token (pk.*). Safe to expose to the SPA — it's
  // scope-restricted to read-only Tile/Style/Static API calls. Used by
  // dashboard panels (GeoActivityPanel) for a real basemap.
  mapboxToken: z.string().optional(),
});

export type AppConfig = z.infer<typeof configSchema>;

let _config: AppConfig;

export async function loadConfig(): Promise<AppConfig> {
  if (_config) return _config;
  if (import.meta.env.DEV) {
    _config = configSchema.parse({
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL || "http://localhost:8000",
      cognitoPoolId: import.meta.env.VITE_COGNITO_POOL_ID || "",
      cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
      cognitoRegion: import.meta.env.VITE_COGNITO_REGION || "eu-central-1",
      environment: "development",
    });
  } else {
    const res = await fetch("/config.json");
    if (!res.ok) {
      document.getElementById("root")!.innerHTML =
        '<div style="padding:2rem;font-family:monospace;color:#dc2626">Failed to load configuration. Please contact support.</div>';
      throw new Error("Failed to load config.json");
    }
    _config = configSchema.parse(await res.json());
  }
  return _config;
}

export function getConfig(): AppConfig {
  if (!_config) throw new Error("Config not loaded. Call loadConfig() first.");
  return _config;
}
